import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { getErrorMessage } from "@/lib/errorMessage";
import { useQuery, queryOptions, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  Copy,
  Loader2,
  Share2,
  Users,
  Wallet,
  Clock,
  Gift,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { getMyReferralCode, type MyReferralCode } from "@/lib/loyalty/referral-code.functions";
import { getMyTruCoinsLedger, type LedgerPage } from "@/lib/loyalty/customer-ledger.functions";
import {
  getMyReferralTimeline,
  type ReferralTimelineItem,
  type ReferralTimelineResponse,
} from "@/lib/loyalty/referral-timeline.functions";
import { formatCoinsWithRupees } from "@/lib/loyalty/coins";

export const Route = createFileRoute("/_authenticated/referral")({
  beforeLoad: async () => {
    const code = await getMyReferralCode();
    if (!code.referralCode) throw redirect({ to: "/account", search: { view: "rewards" } });
  },
  head: () => ({
    meta: [
      { title: "Refer & Earn · TruWe" },
      { name: "description", content: "Customer referral programme availability and your TruWe rewards." },
      { property: "og:title", content: "Refer & Earn · TruWe" },
      { property: "og:description", content: "Customer referral programme availability and your TruWe rewards." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  errorComponent: ({ error, reset }: { error: unknown; reset: () => void }) => (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="font-display text-2xl font-semibold">Couldn't load Refer & Earn</h1>
      <p className="mt-2 text-sm text-muted-foreground">{getErrorMessage(error)}</p>
      <Button className="mt-4" onClick={reset}>
        Retry
      </Button>
    </div>
  ),
  notFoundComponent: () => <div className="p-12 text-center">Not found</div>,
  loader: ({ context }) => {
    context.queryClient.prefetchQuery(referralCodeOptions());
    context.queryClient.prefetchQuery(ledgerOptions());
    context.queryClient.prefetchQuery(timelineOptions());
  },
  component: ReferralPage,
});

const referralCodeOptions = () =>
  queryOptions<MyReferralCode, Error>({
    queryKey: ["my-referral-code"],
    queryFn: () => getMyReferralCode(),
    staleTime: 5 * 60 * 1000,
  });

const ledgerOptions = () =>
  queryOptions<LedgerPage, Error>({
    queryKey: ["trucoins-ledger", "referral-page"],
    queryFn: () => getMyTruCoinsLedger({ data: { limit: 1, offset: 0 } }),
    staleTime: 30 * 1000,
  });

const timelineOptions = () =>
  queryOptions<ReferralTimelineResponse, Error>({
    queryKey: ["referral-timeline"],
    queryFn: () => getMyReferralTimeline(),
    staleTime: 30 * 1000,
  });

function ReferralPage() {
  const queryClient = useQueryClient();
  const codeQuery = useQuery(referralCodeOptions());
  const ledgerQuery = useQuery(ledgerOptions());
  const timelineQuery = useQuery(timelineOptions());

  const isLoading = codeQuery.isLoading || ledgerQuery.isLoading || timelineQuery.isLoading;
  const error = codeQuery.error || ledgerQuery.error || timelineQuery.error;

  const referralCode = codeQuery.data?.referralCode ?? null;
  const summary = ledgerQuery.data?.summary ?? null;
  const progress = ledgerQuery.data?.referralProgress ?? null;
  const items = timelineQuery.data?.items ?? [];

  const earnedPaise = items
    .filter((i) => i.status === "rewarded" && i.rewardPaise > 0)
    .reduce((sum, i) => sum + i.rewardPaise, 0);
  const pendingItems = items.filter(
    (i) => i.status === "attributed" || i.status === "qualified" || i.status === "pending_review",
  );
  const pendingPaise = pendingItems.reduce((sum, i) => sum + i.rewardPaise, 0);
  const successfulCount = progress?.successfulReferrals ?? items.filter((i) => i.status === "rewarded").length;
  // Config-driven: first referrer slab is the honest "next reward" fallback.
  const nextRewardPaise = progress?.nextSlabPaise ?? progress?.slabs?.[0]?.paise ?? 200 * 50;
  const friendDiscountPaise = progress?.friendDiscountPaise || 10_000;
  const friendDiscountLabel = `₹${(friendDiscountPaise / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-[radial-gradient(ellipse_at_top,theme(colors.brand-soft/40),transparent_60%)]">
      <div className="mx-auto max-w-3xl px-4 py-8 md:py-14">
        <Link
          to="/account"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition hover:text-brand-deep"
        >
          <ArrowLeft size={16} /> Back to account
        </Link>

        <div className="mt-6 rounded-3xl border border-border/70 bg-card/80 p-6 shadow-[0_1px_0_rgba(0,0,0,0.02),0_20px_60px_-30px_rgba(30,94,255,0.25)] backdrop-blur-sm md:p-8">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-full bg-amber-100 text-amber-600">
              <Gift size={22} strokeWidth={1.75} />
            </div>
            <div>
              <h1 className="font-display text-2xl font-semibold tracking-tight text-brand-deep md:text-3xl">
                Refer & Earn
              </h1>
              <p className="text-sm text-muted-foreground">
                Give {friendDiscountLabel} off · Get TruCoins for every friend who buys
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className="mt-10 flex flex-col items-center justify-center gap-3 py-8 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              <p className="text-sm">Loading your referral status…</p>
            </div>
          ) : error ? (
            <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
              <p>Couldn't load your referral status.</p>
              <Button
                className="mt-4"
                variant="outline"
                size="sm"
                onClick={() => {
                  queryClient.invalidateQueries({ queryKey: ["my-referral-code"] });
                  queryClient.invalidateQueries({ queryKey: ["trucoins-ledger"] });
                  queryClient.invalidateQueries({ queryKey: ["referral-timeline"] });
                }}
              >
                Retry
              </Button>
            </div>
          ) : (
            <>
              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                <StatPill
                  icon={<Wallet size={18} className="text-emerald-600" />}
                  label="Earned from referrals"
                  value={formatCoinsWithRupees(earnedPaise)}
                />
                <StatPill
                  icon={<Clock size={18} className="text-amber-600" />}
                  label="Pending rewards"
                  value={pendingItems.length > 0 ? formatCoinsWithRupees(pendingPaise) : "None yet"}
                  hint={
                    pendingItems.length > 0
                      ? `${pendingItems.length} friend${pendingItems.length === 1 ? "" : "s"} in progress`
                      : undefined
                  }
                />
                <StatPill
                  icon={<Users size={18} className="text-brand" />}
                  label="Successful referrals"
                  value={String(successfulCount)}
                  hint={
                    successfulCount === 0
                      ? "Share your link to start"
                      : `Next reward: ${formatCoinsWithRupees(nextRewardPaise)}`
                  }
                />
              </div>

              <div className="mt-8 rounded-2xl border border-border/70 bg-card p-5 sm:p-6">
                <h2 className="font-display text-base font-semibold text-brand-deep">Your referral link</h2>
                <p className="text-sm text-muted-foreground">
                  Friends get {friendDiscountLabel} off their first eligible prepaid order of ₹1,000+ ex-GST. You earn TruCoins 7 days after their delivery.
                </p>
                <ShareBox referralCode={referralCode} friendDiscountLabel={friendDiscountLabel} />
              </div>

              <div className="mt-8">
                <h2 className="font-display text-base font-semibold text-brand-deep">Referral status</h2>
                <ReferralList items={items} />
              </div>

              {summary && summary.availablePaise > 0 && (
                <div className="mt-8 rounded-2xl border border-brand/20 bg-brand-soft/40 p-4 text-sm text-brand-deep">
                  <span className="font-semibold">TruCoins balance:</span>{" "}
                  {formatCoinsWithRupees(summary.availablePaise)} available.{" "}
                  <Link to="/account" className="font-semibold underline-offset-2 hover:underline">
                    View wallet
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </main>
  );
}

function StatPill({
  icon,
  label,
  value,
  hint,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 transition hover:border-brand/30">
      <div className="flex items-center gap-2">
        {icon}
        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
      </div>
      <p className="mt-2 font-display text-lg font-semibold tabular-nums text-brand-deep">{value}</p>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function ShareBox({ referralCode, friendDiscountLabel }: { referralCode: string | null; friendDiscountLabel: string }) {
  const [copied, setCopied] = useState(false);
  const hasCode = !!referralCode;
  const link = `${typeof window !== "undefined" ? window.location.origin : "https://truwe.in"}/auth?ref=${encodeURIComponent(
    referralCode ?? "",
  )}`;

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  async function copy() {
    if (!referralCode) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy link");
    }
  }

  async function share() {
    if (!referralCode) {
      toast.message("Your referral code is being set up");
      return;
    }
    const shareData = {
      title: "TruWe — Precision Wellness",
      text: `Use my code ${referralCode} for ${friendDiscountLabel} off your first TruWe order.`,
      url: link,
    };
    try {
      if (typeof navigator !== "undefined" && "share" in navigator) {
        await (navigator as Navigator & { share: (d: ShareData) => Promise<void> }).share(shareData);
        return;
      }
    } catch {
      /* fall through to copy */
    }
    await copy();
  }

  return (
    <div className="mt-4">
      <div className="flex w-full items-stretch rounded-xl border-2 border-dashed border-amber-400/60 bg-amber-50/40">
        <div className="min-w-0 flex-1 px-4 py-3 text-center font-display text-xl font-bold tracking-[0.12em] text-amber-700 sm:text-2xl">
          {hasCode ? referralCode : "••••••"}
        </div>
        <button
          type="button"
          onClick={copy}
          disabled={!hasCode}
          className="grid w-14 place-items-center border-l-2 border-dashed border-amber-400/60 text-amber-700 transition hover:bg-amber-100/60 disabled:cursor-not-allowed disabled:opacity-45"
          aria-label={copied ? "Link copied" : "Copy referral link"}
        >
          {copied ? <CheckCircle2 size={20} /> : <Copy size={20} />}
        </button>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={copy}
          disabled={!hasCode}
          className="flex items-center justify-center gap-2 rounded-xl bg-[#0e0e0e] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0e0e0e]/90 disabled:cursor-not-allowed disabled:opacity-45"
        >
          {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
          {copied ? "Copied" : "Copy link"}
        </button>
        <button
          type="button"
          onClick={share}
          className="flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-semibold text-brand-deep transition hover:bg-muted/50"
        >
          <Share2 size={16} />
          Share
        </button>
      </div>
    </div>
  );
}

function ReferralList({ items }: { items: ReferralTimelineItem[] }) {
  if (items.length === 0) {
    return (
      <div className="mt-3 rounded-2xl border border-dashed border-border bg-card/60 p-8 text-center">
        <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-amber-50 text-amber-500">
          <Users size={22} />
        </div>
        <p className="mt-4 text-sm text-foreground">No referrals yet.</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Share your link with friends and watch your TruCoins grow.
        </p>
      </div>
    );
  }

  return (
    <ul className="mt-3 space-y-3">
      {items.map((item) => (
        <li
          key={item.id}
          className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4"
        >
          <div className="min-w-0">
            <p className="font-medium text-brand-deep">
              {item.displayName}
              {item.displayHint && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">{item.displayHint}</span>
              )}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {statusLabel(item)} · {item.referralCode ? `via ${item.referralCode}` : "referral"}
            </p>
          </div>
          <div className="shrink-0 text-right">
            <p className="font-display text-sm font-semibold text-brand-deep">
              {item.rewardPaise > 0 ? formatCoinsWithRupees(item.rewardPaise) : "—"}
            </p>
            <p className="text-[10px] text-muted-foreground">
              {item.creditedAt
                ? new Date(item.creditedAt).toLocaleDateString("en-IN", { dateStyle: "medium" })
                : item.firstOrderAt
                  ? "Order placed"
                  : item.signedUpAt
                    ? "Signed up"
                    : "Invited"}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function statusLabel(item: ReferralTimelineItem): string {
  if (item.status === "reversed" || item.reversedAt) return "Reversed";
  if (item.status === "rejected") return "Not eligible";
  if (item.status === "pending_review") return "Under review";
  if (item.creditedAt) return "Credited";
  if (item.firstOrderAt) return "Order placed";
  if (item.signedUpAt) return "Signed up";
  return "Invited";
}
